import { platform } from '~/platform/index.js';
import { FREE_AVATARS, avatarRigDir, isAvatarId, type AvatarId } from '~/store/avatars.js';
import { type RigConfig, rigLayers } from './AvatarRig.js';

/** A face's rig before its folder is known: everything but `dir`. */
type CastFace = Omit<RigConfig, 'dir'>;

/**
 * Each catalog face's rig: the puppet `AvatarRig` cuts from its approved
 * masters, whose layers ship under `public/avatars/<id>/`.
 *
 * A person wears it where the result is read off the face: the game-over
 * wells, with the winner's face or the loser's (`docs/ux/cross/game-over.md`,
 * Mitch 2026-09-02). Everywhere else a person wears the still, the same
 * neutral drawing at rest. The Shared - Avatars sketch animates this same
 * table, so the sketch and the product cannot disagree about a face.
 *
 * Most faces are a continuous bust with no chin layer. Four keep a neck joint,
 * and three of those draw a jaw over the collar on some drawings. The pivot
 * and the loss retake's fit live in `ui/css/avatar.css` under these classes;
 * the names are spelled out so a renamed face is one grep. Scout comes first
 * because the sketch opens on him, the reference rig.
 */
const CAST: Readonly<Record<AvatarId, CastFace>> = {
  scout: { slug: 'scout', pivotClass: 'rig-scout', chinDrawings: ['win', 'loss'] },
  whiz: { slug: 'whiz', wholeBust: true, pivotClass: 'rig-whiz', chinDrawings: [] },
  nana: { slug: 'nana', wholeBust: true, pivotClass: 'rig-nana', chinDrawings: [] },
  pixel: { slug: 'pixel', wholeBust: true, pivotClass: 'rig-pixel', chinDrawings: [] },
  skunk: { slug: 'skunk', wholeBust: true, pivotClass: 'rig-skunk', chinDrawings: [] },
  cat: { slug: 'cat', wholeBust: true, pivotClass: 'rig-cat', chinDrawings: [] },
  champ: { slug: 'champ', wholeBust: true, pivotClass: 'rig-champ', chinDrawings: [] },
  chef: { slug: 'chef', wholeBust: true, pivotClass: 'rig-chef', chinDrawings: [] },
  coach: { slug: 'coach', wholeBust: true, pivotClass: 'rig-coach', chinDrawings: [] },
  cougar: { slug: 'cougar', wholeBust: true, pivotClass: 'rig-cougar', chinDrawings: [] },
  crab: { slug: 'crab', wholeBust: true, pivotClass: 'rig-crab', chinDrawings: [] },
  diva: { slug: 'diva', wholeBust: true, pivotClass: 'rig-diva', chinDrawings: [] },
  doc: { slug: 'doc', pivotClass: 'rig-doc', chinDrawings: ['win'] },
  dog: { slug: 'dog', pivotClass: 'rig-dog', chinDrawings: [] },
  dragon: { slug: 'dragon', wholeBust: true, pivotClass: 'rig-dragon', chinDrawings: [] },
  duke: { slug: 'duke', pivotClass: 'rig-duke', chinDrawings: ['win'] },
  flex: { slug: 'flex', wholeBust: true, pivotClass: 'rig-flex', chinDrawings: [] },
  fox: { slug: 'fox', wholeBust: true, pivotClass: 'rig-fox', chinDrawings: [] },
  gigi: { slug: 'gigi', wholeBust: true, pivotClass: 'rig-gigi', chinDrawings: [] },
  grind: { slug: 'grind', wholeBust: true, pivotClass: 'rig-grind', chinDrawings: [] },
  hare: { slug: 'hare', wholeBust: true, pivotClass: 'rig-hare', chinDrawings: [] },
  hay: { slug: 'hay', wholeBust: true, pivotClass: 'rig-hay', chinDrawings: [] },
  horse: { slug: 'horse', wholeBust: true, pivotClass: 'rig-horse', chinDrawings: [] },
  magpie: { slug: 'magpie', wholeBust: true, pivotClass: 'rig-magpie', chinDrawings: [] },
  major: { slug: 'major', wholeBust: true, pivotClass: 'rig-major', chinDrawings: [] },
  gala: { slug: 'gala', wholeBust: true, pivotClass: 'rig-gala', chinDrawings: [] },
  mime: { slug: 'mime', wholeBust: true, pivotClass: 'rig-mime', chinDrawings: [] },
  moonbeam: { slug: 'moonbeam', wholeBust: true, pivotClass: 'rig-moonbeam', chinDrawings: [] },
  mouse: { slug: 'mouse', wholeBust: true, pivotClass: 'rig-mouse', chinDrawings: [] },
  zora: { slug: 'zora', wholeBust: true, pivotClass: 'rig-zora', chinDrawings: [] },
  owl: { slug: 'owl', wholeBust: true, pivotClass: 'rig-owl', chinDrawings: [] },
  patch: { slug: 'patch', wholeBust: true, pivotClass: 'rig-patch', chinDrawings: [] },
  pops: { slug: 'pops', wholeBust: true, pivotClass: 'rig-pops', chinDrawings: [] },
  sarge: { slug: 'sarge', wholeBust: true, pivotClass: 'rig-sarge', chinDrawings: [] },
  skipper: { slug: 'skipper', wholeBust: true, pivotClass: 'rig-skipper', chinDrawings: [] },
  sleuth: { slug: 'sleuth', wholeBust: true, pivotClass: 'rig-sleuth', chinDrawings: [] },
  spike: { slug: 'spike', wholeBust: true, pivotClass: 'rig-spike', chinDrawings: [] },
  star: { slug: 'star', wholeBust: true, pivotClass: 'rig-star', chinDrawings: [] },
  suit: { slug: 'suit', wholeBust: true, pivotClass: 'rig-suit', chinDrawings: [] },
  tank: { slug: 'tank', wholeBust: true, pivotClass: 'rig-tank', chinDrawings: [] },
  timber: { slug: 'timber', wholeBust: true, pivotClass: 'rig-timber', chinDrawings: [] },
  unicorn: { slug: 'unicorn', wholeBust: true, pivotClass: 'rig-unicorn', chinDrawings: [] },
  vibe: { slug: 'vibe', wholeBust: true, pivotClass: 'rig-vibe', chinDrawings: [] },
};

/**
 * Every catalog face's rig, in the sketch's order. The loss drawing wears the
 * hook its retake's fit selects.
 */
export const CAST_RIGS: Readonly<Record<AvatarId, RigConfig>> = Object.fromEntries(
  Object.entries(CAST).map(([id, face]) => [
    id,
    { ...face, dir: avatarRigDir(id as AvatarId), lossClass: 'rig-loss-drawing' },
  ]),
) as Record<AvatarId, RigConfig>;

/**
 * The rig a person wears for a result, or none.
 *
 * None for Patch: his parrot is cut out as a puppet of its own, which only the
 * sketch draws (`useParrot`), so his rig alone would be Patch without the bird
 * he wears everywhere else. He keeps his still until the parrot ships.
 */
export function personRig(look: AvatarId): RigConfig | undefined {
  return look === 'patch' ? undefined : CAST_RIGS[look];
}

/**
 * Load the rig of the face this player wears, so an offline game over can
 * play it. The free faces' rigs are precached and a members' face's is not
 * (about 12.6 MB for all 39), so the service worker keeps each layer once it
 * has loaded (`vite.config.ts`, runtime caching). This loads them ahead of the
 * first game over. The apps carry every file in the bundle and need none of it.
 */
export function warmOwnRig(avatarId: string | null | undefined): void {
  if (platform().isNative || !isAvatarId(avatarId) || FREE_AVATARS.includes(avatarId)) return;
  const rig = personRig(avatarId);
  if (rig === undefined) return;
  for (const src of rigLayers(rig)) new Image().src = src;
}
